import { FaqSectionType, zFaqSection } from "#app/types/schema.ts";
import { SectionDefaultProps } from "#app/types/type.ts";
import { stegaClean } from "@sanity/client/stega";
import { Heading } from "./text";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { PortableText, PortableTextComponents } from "@portabletext/react";
import { Link } from "react-router";
import { resolveInternalLinkUrl } from "#app/utils/misc.tsx";


type FaqSectionProps = SectionDefaultProps & {
    data: FaqSectionType;
}

const portableTextComponents: PortableTextComponents = {
    marks: {
        link: ({ children, value }) => {
            const href = value?.href || '';
            const isExternal = href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:');
            const openInNewTab = value?.blank === true; // Check the 'blank' property fetched via GROQ

            return (
                <Link 
                to={href} 
                className="text-primary hover:underline" 
                prefetch={isExternal ? 'intent' : undefined}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                >
                    {children}
                </Link>
            );
        },
        internalLink: ({ children, value }) => {
            // 'value' now contains { _key, _type: 'internalLink', reference: {...}, slug: '...', docType: '...' }
            // thanks to the GROQ query modification.
            const urlPath = resolveInternalLinkUrl(value?.docType, value?.slug);

            if (!urlPath) {
                // Fallback for missing link data or unhandled type
                return <span className="text-red-500 italic">(Invalid Link) {children}</span>;
            }

            return (
                <Link to={urlPath} className="text-primary hover:underline" prefetch="intent">
                    {children}
                </Link>
            );
        },
        customPathLink: ({ children, value }) => {
            const path = value?.path;
    
            if (!path) {
              // Fallback if path is missing
              return <span className="text-red-500 italic">(Invalid Custom Link Path) {children}</span>;
            }
    
            // Use React Router Link with the predefined path
            return (
              <Link to={path} className="text-primary hover:underline" prefetch="intent">
                {children}
              </Link>
            );
        },
    },
};

export default function FaqSection({ data, encodeDataAttribute }: FaqSectionProps) {
    const faqSectionData = stegaClean(data);
    const { title, headingLevel, faqs } = faqSectionData;
    return (
        <div>
            <div className='container'>
                {title && (
                <Heading as={headingLevel}>
                    {title}
                </Heading>
                )}
                <Accordion type="single" collapsible className="w-full mt-10">
                    {faqs?.map((faq, index) => (
                        <AccordionItem key={faq._key} value={faq._key} className="w-full">
                            <AccordionTrigger>{faq.question}</AccordionTrigger>
                            <AccordionContent className="prose dark:prose-invert">
                                <PortableText value={faq.answer} components={portableTextComponents} />
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </div>
    )
}